import {BranchName, Label, ProgressBar, RelativeTime, type RelativeTimeProps} from '@primer/react'
import {ArrowDownIcon, ArrowUpIcon, LockIcon, UnlockIcon, type Icon} from '@primer/octicons-react'
import {CopyToClipboardIconButton} from '@github-ui/copy-to-clipboard/IconButton'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {ownerAvatarPath, ownerPath, repositoryPath} from '@github-ui/paths'
import {Link} from '@github-ui/react-router'
import {ConnectionStatus, getReadableSessionState, SessionState, type Session} from '../../types/session'
import type {Environment} from '@github-ui/agent-sessions-types/environment'
import type {HydratedAPITask} from '../../types/api-task'
import type {RelayConnectionProgress, RelayHostInfo, RelayStatus} from '@github-ui/ahp-relay/relay-connection-runtime'
import {
  contextUsageRatio,
  toAiCredits,
  type RelayContextUsage,
  type RelaySessionUsage,
  type RelayTokenUsage,
} from '@github-ui/ahp-relay/session-usage'
import {useAgentProfile} from '../../contexts/agent-profile-context'
import {useAgentInfo} from '@github-ui/copilot-shared-ui/agents/use-agent-info'
import {isCopilotCLICompatibleAgent} from '@github-ui/copilot-shared-ui/agents/agent-helpers'
import {computeConnectionStatus} from '../../utils/connection-status'
import {useSelectedModel} from '../../hooks/use-selected-model'
import {trimModelName} from '../../utils/trim-model-name'
import {
  ENVIRONMENT_KIND_ICONS,
  displayDirectory,
  getEnvironmentDisplayName,
  resolveEnvironmentStatus,
} from '@github-ui/copilot-shared-ui/environments/environment-display'
import {SessionDetailMetadata} from './StickyTaskChatHeaderMenu'
import menuStyles from './StickyTaskChatHeaderMenu.module.css'
import {getEnvironmentFreshness} from '../../hooks/use-environment-details-visibility'
import type {SessionAgentResolution} from './use-session-agent-resolution'

export interface StickyHeaderSessionDetailsProps {
  session?: Session
  /** The session's selected AHP agent, resolved from its live or saved turn history. */
  agentResolution?: SessionAgentResolution
  /** Whether the caller identified this session as AHP-backed. Omit when its identity is unknown. */
  isAhpSession?: boolean
  task?: HydratedAPITask
  currentUserIsOwner?: boolean
  /** The resolved AHP environment, when the session is bound to one. */
  environment?: Environment
  /** Host facts learned from the live AHP handshake, when a relay is connected. */
  hostInfo?: RelayHostInfo
  /** Token totals and context-window occupancy reported by the live AHP host. */
  usage?: RelaySessionUsage
  /**
   * Spend to display, in raw billionths of an AI credit, already resolved by the
   * header from live AHP usage or the persisted per-session totals. Undefined
   * hides the row.
   */
  spendNanoAiu?: number
  /** Whether the AHP host is answering on the live relay right now. */
  relayReachable?: boolean
  /** Identity-matched progress for a current wake or reconnect attempt. */
  connectionProgress?: RelayConnectionProgress
  /** Identity-matched current relay status. */
  relayStatus?: RelayStatus
  /**
   * Whether the task URL covers more than one backend Session, which hides the
   * session ID because spend is summed across all of them. Keyed off the session
   * count rather than relay reachability, so an asleep or disconnected
   * single-session task keeps its ID instead of flickering on reconnect.
   */
  isMultiSession?: boolean
  branchName?: string
  /** Display name of the task author for managed Chat environments. */
  owner?: string
  /** Raw login of the task author for managed Chat links and avatars. */
  ownerLogin?: string
  /** Optional avatar URL override for managed Chat fixtures. */
  ownerAvatarUrl?: string
  /** Repository name for managed Cloud environments. */
  repository?: string
  /** Feature-flag-gated environment kind while the environment record loads. */
  fallbackEnvironmentKind?: Environment['kind']
  /** Whether the selected environment record is still loading. */
  environmentLoading?: boolean
  /** The selected environment lookup or refresh failed. */
  environmentError?: Error | null
  dataUpdatedAt?: number
}

const tokenFormatter = new Intl.NumberFormat('en-US', {notation: 'compact', maximumFractionDigits: 1})
const percentFormatter = new Intl.NumberFormat('en-US', {style: 'percent', maximumFractionDigits: 0})
const creditFormatter = new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})

export function sentenceCaseRelativeTimeText(text: string): string {
  const [firstCharacter, ...remainingCharacters] = text
  return firstCharacter ? `${firstCharacter.toLocaleUpperCase()}${remainingCharacters.join('')}` : text
}

export const sentenceCaseRelativeTime: NonNullable<RelativeTimeProps['onRelativeTimeUpdated']> = event => {
  const relativeTime = event.currentTarget
  if (!(relativeTime instanceof HTMLElement)) return

  const renderedTime = relativeTime.shadowRoot?.querySelector('[part="root"]')
  if (renderedTime) renderedTime.textContent = sentenceCaseRelativeTimeText(event.newText)
}

/**
 * Two decimals read as money, but an AI credit is a billion nano-AIU, so real
 * spend below a hundredth of a credit would round to `0.00` and be
 * indistinguishable from a session that spent nothing.
 */
function formatCredits(credits: number): string {
  if (credits > 0 && credits < 0.005) return '<0.01'
  return creditFormatter.format(credits)
}

function formatTokens(count: number): string {
  return tokenFormatter.format(count)
}

function TokenUsageRow({tokens}: {tokens: RelayTokenUsage}) {
  return (
    <SessionDetailMetadata label="Tokens" valueClassName={menuStyles.stackedValue}>
      <span className={menuStyles.tokenLines} data-testid="usage-tokens">
        <TokenLine
          icon={ArrowUpIcon}
          label="Input"
          value={tokens.input}
          note={tokens.cached > 0 ? `${formatTokens(tokens.cached)} cached` : undefined}
        />
        <TokenLine
          icon={ArrowDownIcon}
          label="Output"
          value={tokens.output}
          note={tokens.reasoning > 0 ? `${formatTokens(tokens.reasoning)} reasoning` : undefined}
        />
      </span>
      {tokens.partial && (
        <span className={menuStyles.usageNote} data-testid="usage-partial">
          Excludes turns not yet loaded
        </span>
      )}
    </SessionDetailMetadata>
  )
}

function TokenLine({icon: Icon, label, value, note}: {icon: Icon; label: string; value: number; note?: string}) {
  return (
    <span className={menuStyles.tokenLine}>
      <Icon size={12} aria-hidden="true" className={menuStyles.tokenIcon} />
      <span className="sr-only">{`${label}: `}</span>
      {formatTokens(value)}
      {note && <span className={menuStyles.usageNote}>{`(${note})`}</span>}
    </span>
  )
}

function ContextUsageRow({context}: {context: RelayContextUsage}) {
  const ratio = contextUsageRatio(context)
  const percent = percentFormatter.format(ratio)

  return (
    <SessionDetailMetadata label="Context" valueClassName={menuStyles.stackedValue}>
      <span className={menuStyles.usageSummary} data-testid="usage-context">
        {`${percent} · ${formatTokens(context.current)}`}
      </span>
      <ProgressBar
        className={menuStyles.contextMeter}
        barSize="small"
        progress={ratio * 100}
        bg="accent.emphasis"
        aria-label={`Context window ${percent} used`}
      />
    </SessionDetailMetadata>
  )
}

function SessionModelDetail({model}: {model: string}) {
  const selectedModel = useSelectedModel(model)
  const modelName = selectedModel?.displayName ?? trimModelName(model)

  return <SessionDetailMetadata label="Most recent model">{modelName}</SessionDetailMetadata>
}

/**
 * The session-detail rows shown in the sticky header popover. AHP-backed
 * sessions additionally surface facts learned from the live relay handshake,
 * which the polled environments record does not carry.
 */
export function StickyHeaderSessionDetails({
  session,
  agentResolution,
  isAhpSession,
  task,
  currentUserIsOwner = false,
  environment,
  hostInfo,
  usage,
  spendNanoAiu,
  relayReachable = false,
  connectionProgress,
  relayStatus,
  isMultiSession = false,
  branchName,
  owner,
  ownerLogin,
  ownerAvatarUrl,
  repository,
  fallbackEnvironmentKind,
  environmentLoading = false,
  environmentError,
  dataUpdatedAt,
}: StickyHeaderSessionDetailsProps) {
  const agentProfile = useAgentProfile()
  const {data: agentInfo, isLoading: isAgentInfoLoading} = useAgentInfo(session?.agent_id, session?.agent_slug)
  const isCLI = agentProfile.interactiveSession
  const isCLISession = isCLI && isCopilotCLICompatibleAgent(session?.agent_slug ?? agentInfo?.slug ?? agentProfile.slug)
  const customAgentName =
    isCLI &&
    isAhpSession === false &&
    !session?.environment_id &&
    !task?.current_environment?.id &&
    !agentResolution &&
    !isMultiSession &&
    !task?.custom_agent?.is_automation
      ? task?.custom_agent?.name?.trim()
      : undefined
  const unresolvedAgentLabel = session?.state === SessionState.Queued ? 'Waiting for session…' : 'Agent unavailable'
  const agentName =
    agentResolution?.kind === 'selected'
      ? agentResolution.displayName
      : agentResolution?.kind === 'loading'
        ? 'Loading agent…'
        : agentResolution?.kind === 'unavailable'
          ? unresolvedAgentLabel
          : isCLISession && isAhpSession === undefined && !agentResolution
            ? unresolvedAgentLabel
            : customAgentName ||
              agentInfo?.display_name?.trim() ||
              (isAgentInfoLoading ? 'Loading agent…' : unresolvedAgentLabel)

  const environmentName = environment
    ? getEnvironmentDisplayName(environment)
    : isCLI
      ? (agentProfile.secondaryLabel ?? 'CLI')
      : 'Cloud'
  const EnvironmentIcon = environment ? ENVIRONMENT_KIND_ICONS[environment.kind] : undefined
  const environmentStatus = environment
    ? resolveEnvironmentStatus(environment, relayReachable, connectionProgress, relayStatus)
    : undefined

  // CLI sessions have no environment record, so their liveness comes from the
  // task's staleness deadline instead of an environment status.
  const connectionStatus =
    !environment && isCLI ? computeConnectionStatus(task?.stale_at, true, session?.state, dataUpdatedAt) : undefined
  const connectionLabel =
    connectionStatus === ConnectionStatus.Connected
      ? 'Connected'
      : connectionStatus === ConnectionStatus.Disconnected
        ? 'Disconnected'
        : undefined

  // Keep the gated environment kind while the full environment record is
  // loading so managed sessions never briefly expose the creator's path.
  const environmentKind = environment?.kind ?? fallbackEnvironmentKind
  // A managed Cloud chat that isn't scoped to a repository has no NWO to show,
  // so fall back to its owner - same rendering path managed-cca uses.
  const isUnscopedSandbox = environmentKind === 'managed-sandbox' && !repository
  const workingDirectory = hostInfo?.defaultDirectory
  const environmentDetail =
    environmentKind === 'managed-cca' || isUnscopedSandbox
      ? owner
      : environmentKind === 'managed-sandbox'
        ? repository
        : workingDirectory
  const isManagedEnvironment = environmentKind === 'managed-cca' || environmentKind === 'managed-sandbox'
  const environmentDetailLabel =
    environmentKind === 'managed-cca' || isUnscopedSandbox
      ? 'Owner'
      : environmentKind === 'managed-sandbox'
        ? 'Repository'
        : 'Working directory'
  const repositoryParts = repository?.split('/')
  const repositoryOwner = repositoryParts?.[0]
  const repositoryName = repositoryParts?.[1]
  const repositoryHref =
    repositoryParts?.length === 2 && repositoryOwner && repositoryName
      ? repositoryPath({owner: repositoryOwner, repo: repositoryName})
      : undefined
  const activeSessions = hostInfo?.activeSessions
  const maxSessions = environment?.capabilities?.max_sessions
  const tokens = usage?.tokens
  const context = usage?.context
  const credits = spendNanoAiu === undefined ? undefined : toAiCredits(spendNanoAiu)
  const environmentFreshness = getEnvironmentFreshness({
    hasEnvironment: !!environment,
    loading: environmentLoading,
    error: !!environmentError,
  })

  return (
    <>
      <SessionDetailMetadata label="Environment">
        {EnvironmentIcon && <EnvironmentIcon size={16} aria-hidden="true" data-testid="environment-kind-icon" />}
        <span className={menuStyles.environmentName} data-testid="environment-name">
          {environmentName}
        </span>
        {environmentStatus && (
          <Label variant={environmentStatus.variant} data-testid="environment-status">
            {environmentStatus.label}
          </Label>
        )}
        {connectionLabel && (
          <Label
            variant={connectionStatus === ConnectionStatus.Connected ? 'success' : 'secondary'}
            data-testid="environment-status"
          >
            {connectionLabel}
          </Label>
        )}
        {environmentFreshness && (
          <span className={menuStyles.usageNote} data-testid="environment-freshness">
            {environmentFreshness}
          </span>
        )}
      </SessionDetailMetadata>
      {session && (
        <SessionDetailMetadata label="Session status">
          <span data-testid="session-status">{getReadableSessionState(session.state)}</span>
        </SessionDetailMetadata>
      )}
      {session?.created_at && (
        <SessionDetailMetadata label="Created">
          <RelativeTime
            date={new Date(session.created_at)}
            tense="past"
            onRelativeTimeUpdated={sentenceCaseRelativeTime}
            data-testid="session-created-at"
          />
        </SessionDetailMetadata>
      )}
      {session && <SessionDetailMetadata label="Agent">{agentName}</SessionDetailMetadata>}
      {session?.model && <SessionModelDetail model={session.model} />}
      {tokens && <TokenUsageRow tokens={tokens} />}
      {context && <ContextUsageRow context={context} />}
      {branchName && (
        <SessionDetailMetadata label="Branch">
          <BranchName as="span" className={menuStyles.branchName}>
            {branchName}
          </BranchName>
          <CopyToClipboardIconButton
            size="small"
            textToCopy={branchName}
            ariaLabel={`Copy branch name ${branchName}`}
          />
        </SessionDetailMetadata>
      )}
      {environmentDetail && (
        <SessionDetailMetadata
          label={environmentDetailLabel}
          valueClassName={isManagedEnvironment ? undefined : menuStyles.monospace}
        >
          <span className={menuStyles.wrappingValue}>
            {(environmentKind === 'managed-cca' || isUnscopedSandbox) && ownerLogin ? (
              <Link className={menuStyles.environmentLink} to={ownerPath({owner: ownerLogin})}>
                <GitHubAvatar
                  className={menuStyles.ownerAvatar}
                  src={ownerAvatarUrl ?? ownerAvatarPath({owner: ownerLogin})}
                  size={16}
                  alt=""
                />
                <span>{environmentDetail}</span>
              </Link>
            ) : environmentKind === 'managed-sandbox' && repositoryHref ? (
              <Link className={menuStyles.environmentLink} to={repositoryHref}>
                {environmentDetail}
              </Link>
            ) : isManagedEnvironment ? (
              environmentDetail
            ) : (
              displayDirectory(environmentDetail)
            )}
          </span>
        </SessionDetailMetadata>
      )}
      {typeof activeSessions === 'number' && (
        <SessionDetailMetadata label={typeof maxSessions === 'number' ? 'Sessions' : 'Active sessions'}>
          {typeof maxSessions === 'number' ? `${activeSessions} / ${maxSessions}` : String(activeSessions)}
        </SessionDetailMetadata>
      )}
      {session && !isMultiSession && (
        <SessionDetailMetadata label="Session ID" valueClassName={menuStyles.monospace}>
          <span className={menuStyles.wrappingValue}>{session.id}</span>
          <span className={menuStyles.copyButtonWrapper}>
            <CopyToClipboardIconButton size="small" textToCopy={session.id} ariaLabel="Copy session ID" />
          </span>
        </SessionDetailMetadata>
      )}
      {credits !== undefined && (
        <SessionDetailMetadata label="Session spend">
          <span data-testid="usage-spend">{`${formatCredits(credits)} AI credits`}</span>
        </SessionDetailMetadata>
      )}
      {agentProfile.privateSessions &&
        (task?.sharing_status === 'shared' || (task?.sharing_status === 'unshared' && currentUserIsOwner)) && (
          <SessionDetailMetadata label="Visibility">
            {task?.sharing_status === 'shared' ? (
              <>
                <UnlockIcon size={16} aria-hidden="true" />
                Visible to anyone with access to this repository
              </>
            ) : (
              <>
                <LockIcon size={16} aria-hidden="true" />
                Only visible to you
              </>
            )}
          </SessionDetailMetadata>
        )}
    </>
  )
}

export interface StickyHeaderStaffSessionDetailsProps {
  environment?: Environment
  hostInfo?: RelayHostInfo
}

function resolveHostSoftware(hostInfo: RelayHostInfo | undefined): string | undefined {
  const name = hostInfo?.serverTitle ?? hostInfo?.serverName
  if (!name) return undefined
  return hostInfo?.serverVersion ? `${name} ${hostInfo.serverVersion}` : name
}

// The negotiated protocol version describes the connection actually in use, so
// it wins over the version the environment record advertises.
function resolveAhpVersion(environment: Environment | undefined, hostInfo: RelayHostInfo | undefined) {
  return hostInfo?.protocolVersion ?? environment?.capabilities?.ahp_version
}

/**
 * The live host's own name, when it disagrees with the environment record. The
 * record is polled, so a host renamed mid-session (`extensions/renameHost`)
 * reports the fresher value here.
 */
function resolveDivergentHostName(
  environment: Environment | undefined,
  hostInfo: RelayHostInfo | undefined,
): string | undefined {
  const hostName = hostInfo?.hostName
  if (!hostName) return undefined
  return hostName !== environment?.name ? hostName : undefined
}

/**
 * Whether the staff group has any row to show. Callers use this to skip the
 * labelled group entirely rather than render an empty bordered section.
 */
export function hasStaffSessionDetails({environment, hostInfo}: StickyHeaderStaffSessionDetailsProps): boolean {
  return (
    !!environment ||
    !!resolveHostSoftware(hostInfo) ||
    !!resolveAhpVersion(environment, hostInfo) ||
    !!resolveDivergentHostName(environment, hostInfo)
  )
}

/**
 * Diagnostic rows restricted to staff: which environment the session actually
 * landed on, plus the values negotiated on the AHP handshake.
 */
export function StickyHeaderStaffSessionDetails({environment, hostInfo}: StickyHeaderStaffSessionDetailsProps) {
  const divergentHostName = resolveDivergentHostName(environment, hostInfo)
  const hostSoftware = resolveHostSoftware(hostInfo)
  const ahpVersion = resolveAhpVersion(environment, hostInfo)

  return (
    <>
      {environment && (
        <SessionDetailMetadata label="Environment ID" valueClassName={menuStyles.monospace}>
          <span className={menuStyles.wrappingValue}>{environment.id}</span>
          <span className={menuStyles.copyButtonWrapper}>
            <CopyToClipboardIconButton size="small" textToCopy={environment.id} ariaLabel="Copy environment ID" />
          </span>
        </SessionDetailMetadata>
      )}
      {divergentHostName && (
        <SessionDetailMetadata label="Host name">
          <span className={menuStyles.wrappingValue} data-testid="host-name">
            {divergentHostName}
          </span>
          <span className={menuStyles.copyButtonWrapper}>
            <CopyToClipboardIconButton size="small" textToCopy={divergentHostName} ariaLabel="Copy host name" />
          </span>
        </SessionDetailMetadata>
      )}
      {hostSoftware && <SessionDetailMetadata label="Host software">{hostSoftware}</SessionDetailMetadata>}
      {ahpVersion && <SessionDetailMetadata label="AHP version">{ahpVersion}</SessionDetailMetadata>}
    </>
  )
}
